import { useMemo, useRef, useState } from 'react';
import { spinnerRing } from '../utils/bookingUtils';
import { buildCalendarMonth, buildDemoSlots } from '../utils/posLandingUtil';
import { POS_SIGNUP_FIELD_MAX } from '../constants/posConst';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The call form's fields, in order. */
const FIELDS = [
  { key: 'name', label: 'Full Name', placeholder: 'Juan dela Cruz', autoComplete: 'name', type: 'text' },
  { key: 'business', label: 'Business Name', placeholder: "Juan's Store & Cafe", autoComplete: 'organization', type: 'text' },
  { key: 'about', label: 'What does your business do?', placeholder: 'Three coffee shops in Cebu City, plus a small pro shop at our court.', multiline: true },
  { key: 'features', label: 'Which custom features are you looking for?', placeholder: 'Consolidated reporting across branches, and a loyalty card for regulars.', multiline: true },
  { key: 'email', label: 'Email Address', placeholder: 'juandelacruz@email.com', autoComplete: 'email', type: 'email' },
  { key: 'phone', label: 'Contact Number', placeholder: '0917...', autoComplete: 'tel', type: 'tel' },
];

/** Midnight today, local time. @returns {Date} */
const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * The custom (Enterprise) plan's checkout: pick a day, pick a 30-minute slot,
 * say what the business needs.
 *
 * @param {object} props
 * @param {boolean} props.busy
 * @param {string} props.error - from a failed save
 * @param {(booking: {date: Date, slot: object, details: object}) => void} props.onSubmit
 * @returns {JSX.Element}
 */
function PosLandingDemoBooking({ busy, error, onSubmit }) {
  const today = useMemo(startOfToday, []);
  const slots = useMemo(buildDemoSlots, []);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [pickDate, setPickDate] = useState(null);
  const [pickSlot, setPickSlot] = useState(null);
  const [details, setDetails] = useState({});
  const [bad, setBad] = useState({});
  const [pickWarn, setPickWarn] = useState(false);
  const inputRefs = useRef({});
  const calendarRef = useRef(null);
  const slotsRef = useRef(null);

  const { lead, days } = buildCalendarMonth(view.year, view.month);
  const atFirstMonth = view.year === today.getFullYear() && view.month === today.getMonth();

  /**
   * Moves the calendar a month either way.
   *
   * @param {number} delta - -1 or 1
   * @returns {void}
   */
  const shiftMonth = (delta) => setView(({ year, month }) => {
    const next = month + delta;
    if (next < 0) return { year: year - 1, month: 11 };
    if (next > 11) return { year: year + 1, month: 0 };
    return { year, month: next };
  });

  /**
   * @param {Date} date
   * @returns {void}
   */
  const handlePickDate = (date) => {
    setPickDate(date);
    setPickSlot(null);
    setPickWarn(false);
    requestAnimationFrame(() => slotsRef.current?.scrollIntoView({ block: 'nearest' }));
  };

  /**
   * @param {React.FormEvent} event
   * @returns {void}
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    if (!pickDate || !pickSlot) {
      setPickWarn(true);
      (pickDate ? slotsRef.current : calendarRef.current)?.scrollIntoView({ block: 'center' });
      return;
    }
    setPickWarn(false);
    const missing = FIELDS.filter((field) => !String(details[field.key] || '').trim()).map((field) => field.key);
    setBad(Object.fromEntries(missing.map((key) => [key, true])));
    if (missing.length) {
      inputRefs.current[missing[0]]?.focus();
      return;
    }
    onSubmit({ date: pickDate, slot: pickSlot, details });
  };

  return (
    <div id="subCall">
      <div className="step-rule"><span>Step 1 — Pick a date</span></div>
      <div className="cal" ref={calendarRef}>
        <div className="cal-head">
          <button type="button" aria-label="Previous month" disabled={atFirstMonth} onClick={() => shiftMonth(-1)}>&lsaquo;</button>
          <b>{MONTHS[view.month]} {view.year}</b>
          <button type="button" aria-label="Next month" onClick={() => shiftMonth(1)}>&rsaquo;</button>
        </div>
        <div className="cal-dow">{WEEKDAYS.map((day) => <span key={day}>{day}</span>)}</div>
        <div className="cal-grid">
          {/* eslint-disable-next-line react/no-array-index-key */}
          {Array.from({ length: lead }, (_, index) => <span className="cal-blank" key={`blank-${index}`} />)}
          {days.map((date) => {
            const past = date < today;
            const isToday = date.getTime() === today.getTime();
            const on = pickDate && date.getTime() === pickDate.getTime();
            return (
              <button
                key={date.getDate()}
                type="button"
                className={`cal-day${isToday ? ' today' : ''}${on ? ' on' : ''}`}
                disabled={past}
                onClick={() => handlePickDate(date)}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {pickDate && (
        <div ref={slotsRef}>
          <div className="step-rule"><span>Step 2 — Pick a 30-minute slot</span></div>
          <p className="slot-note">Demo calls run 6:00 PM to 3:00 AM, Philippine time. Choose one slot.</p>
          <div className="slots">
            {slots.map((slot) => (
              <button
                key={slot.start}
                type="button"
                className={`slot${pickSlot?.start === slot.start ? ' on' : ''}`}
                onClick={() => { setPickSlot(slot); setPickWarn(false); }}
              >
                {slot.label}
                {slot.nextDay && <i>next day</i>}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="step-rule"><span>Step 3 — Tell us about your business</span></div>
      {pickWarn && <p className="pick-warn">Please choose a date and a 30-minute slot first.</p>}
      <form id="callForm" noValidate onSubmit={handleSubmit}>
        <p className="privacy-note">
          Your details are stored securely and used only to prepare your demo,
          build your quote and keep our internal client records. We never sell or share them.
        </p>
        {FIELDS.map((field) => {
          const common = {
            ref: (element) => { inputRefs.current[field.key] = element; },
            name: field.key,
            placeholder: field.placeholder,
            maxLength: POS_SIGNUP_FIELD_MAX[field.key],
            className: bad[field.key] ? 'bad' : undefined,
            value: details[field.key] || '',
            disabled: busy,
            required: true,
            onChange: (event) => {
              setDetails((current) => ({ ...current, [field.key]: event.target.value }));
              if (bad[field.key]) setBad((current) => ({ ...current, [field.key]: false }));
            },
          };
          return (
            <label key={field.key}>
              {field.label} <i>*</i>
              {field.multiline
                ? <textarea rows={3} {...common} />
                : <input type={field.type} autoComplete={field.autoComplete} {...common} />}
            </label>
          );
        })}
        {error && <p className="pick-warn">{error}</p>}
        {busy && <div className="plan-state">{spinnerRing('Booking')}</div>}
        <button type="submit" className="btn btn-primary btn-lg" id="callGo" disabled={busy}>Book my demo call</button>
      </form>
    </div>
  );
}

export default PosLandingDemoBooking;
